<template>
  <v-card class="fill-height" dark>
    <v-card-text class="exercise-list pa-0" :style="{ 'margin-top': initialHeight + 10 + 'px' }">
      <v-list dense :style="getSelectedExercise ? 'overflow-x: hidden;' : ''" class="pa-0 justify-content-center">
        <template v-if="warmupOnly.length">
          <v-list-item class="section-header warmup-marker mt-0 px-0 justify-content-center">
            <v-col cols="6" class="d-flex justify-center orange rounded-xl">
              <v-avatar size="32" large class="orange-dot">
                <v-img src="/img/workout/calories.png" />
              </v-avatar>
              <div class="section-title">Aufwärmen</div>
            </v-col>
          </v-list-item>
          <v-list-item
            v-for="exercise in warmupOnly"
            :key="exercise._id + '-warmup'"
            class="exercise-card exercise-card--warmup mx-auto px-1"
            :class="{ 'exercise-line-completed': isExerciseCompleted(exercise) }"
            @click="open(exercise, $event)"
            :ripple="false">
            <v-card ripple flat class="card-inner" :style="{ border: isExerciseCompleted(exercise) ? '2px solid green' : '2px solid orange' }">
              <v-icon v-if="isExerciseCompleted(exercise)" color="green" small class="completed-checkmark"> mdi-check-circle </v-icon>
              <v-row no-gutters align="center">
                <v-col cols="3" class="d-flex justify-center">
                  <v-img :src="getImageForExercise(exercise)" height="64px" width="64px" class="card-img rounded" />
                </v-col>
                <v-col cols="9" class="pl-2">
                  <div class="title font-weight-bold mb-1">{{ exercise.title }}</div>
                  <div class="subtitle-2">
                    <template v-if="exercise.title === 'Senso Pro'">
                      <span v-if="isExerciseCompleted(exercise)" class="green--text text--lighten-1">Durchgang abgeschlossen</span>
                      <span v-else>Bereit für den Durchgang</span>
                    </template>
                    <template v-else>
                      <span style="position: absolute; bottom: 5px; right: 10px"> {{ exercise.completedSetCount }} / 1 </span>
                      <v-progress-linear :value="exercise.completedSetCount * 100" color="orange" height="4" rounded class="mt-2"></v-progress-linear>
                    </template>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-list-item>
        </template>

        <template v-if="regularExercises.length">
          <v-list-item class="section-header training-marker justify-content-center">
            <v-col cols="6" class="d-flex justify-center rounded-xl grey">
              <v-avatar size="32" large color="rgba(255, 255, 255, 0.05)">
                <v-img src="/img/workout/kettlebell.png" />
              </v-avatar>
              <div class="section-title">Training</div>
            </v-col>
          </v-list-item>
          <template v-for="(exercise, index) in regularExercises">
            <v-list-item
              v-if="!exercise.isSuperset"
              :key="exercise._id + '-regular'"
              class="exercise-card px-1 mx-auto"
              :class="{ 'exercise-line-completed': isExerciseCompleted(exercise) }"
              @click="open(exercise, $event)"
              :ripple="false">
              <v-card
                ripple
                flat
                class="card-inner rounded-xl"
                :class="{
                  'exercise-card-inner--completed': isExerciseCompleted(exercise),
                  'intensity-glow': exercise.method,
                }"
                :style="getCardStyle(exercise)">
                <v-icon v-if="isExerciseCompleted(exercise)" color="green" small class="completed-checkmark">mdi-check-circle</v-icon>
                <v-row no-gutters align="center">
                  <v-col cols="3" class="d-flex justify-center">
                    <v-img :src="getImageForExercise(exercise)" height="64px" width="64px" class="card-img rounded" />
                  </v-col>
                  <v-col cols="9" class="pl-2">
                    <div class="title font-weight-bold mb-1">
                      {{ exercise.title }}
                      <v-chip v-if="exercise.warmup" color="orange" x-small outlined class="intensity-chip ml-1" dark label>
                        <v-icon x-small>mdi-fire</v-icon>
                      </v-chip>
                      <v-chip
                        v-if="exercise.method && intensityTechniqueMapping[exercise.method]"
                        :color="intensityTechniqueMapping[exercise.method].color"
                        x-small
                        outlined
                        class="ml-1 intensity-chip"
                        dark
                        label>
                        <v-icon x-small>{{ intensityTechniqueMapping[exercise.method].icon }}</v-icon>
                      </v-chip>
                    </div>

                    <div class="subtitle-2">
                      <template v-if="exercise.title === 'Senso Pro'">
                        <span v-if="isExerciseCompleted(exercise)" class="green--text text--lighten-1">Durchgang abgeschlossen</span>
                        <span v-else>Bereit für den Durchgang</span>
                      </template>
                      <template v-else>
                        <span style="position: absolute; bottom: 5px; right: 10px"
                          >{{ exercise.completedSetCount }} / {{ exercise.plannedSetCount }}
                        </span>
                        <v-progress-linear
                          v-if="exercise.plannedSetCount > 0"
                          :value="(exercise.completedSetCount / exercise.plannedSetCount) * 100"
                          :color="isExerciseCompleted(exercise) ? 'green' : 'primary'"
                          height="4"
                          rounded
                          class="mt-2"></v-progress-linear
                      ></template>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-list-item>

            <v-list-item
              v-else
              :key="exercise.supersetId + '-superset'"
              class="exercise-card px-1 mx-auto"
              :class="{ 'exercise-line-completed': isExerciseCompleted(exercise) }"
              @click="open(exercise, $event)"
              :ripple="false">
              <v-card
                ripple
                flat
                class="card-inner rounded-xl superset-card-inner"
                :class="{ 'exercise-card-inner--completed': isExerciseCompleted(exercise) }">
                <v-icon v-if="isExerciseCompleted(exercise)" color="green" small class="completed-checkmark">mdi-check-circle</v-icon>
                <div class="superset-label">
                  <v-icon small left>mdi-arrow-collapse-all</v-icon>
                  Supersatz
                </div>
                <v-row no-gutters align="center">
                  <v-col cols="3" class="d-flex flex-column align-center justify-center">
                    <v-avatar size="40" class="mb-1 elevation-2">
                      <v-img :src="getImageForExercise(exercise.exercises[0])" />
                    </v-avatar>
                    <v-avatar size="40" class="mt-1 elevation-2">
                      <v-img :src="getImageForExercise(exercise.exercises[1])" />
                    </v-avatar>
                  </v-col>
                  <v-col cols="9" class="pl-3">
                    <div v-for="subEx in exercise.exercises" :key="subEx._id" class="mb-1">
                      <div class="title font-weight-bold" style="line-height: 1.2">{{ subEx.title }}</div>
                      <div class="subtitle-2">{{ getCompletedSetsForSubExercise(exercise, subEx).length }} / {{ subEx.sets.length }} Sätze</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-list-item>
          </template>
          <v-list-item class="section-header my-0 pb-7 justify-content-center" @click="promptFinishWorkout">
            <v-col cols="6" class="d-flex justify-center primary rounded-xl">
              <div class="section-title">Training abschliessen</div>
            </v-col>
          </v-list-item>
        </template>
      </v-list>
    </v-card-text>
    <v-dialog v-model="showIncompleteDialog" max-width="450px" dark>
      <v-card class="rounded-xl pa-2" style="background: #2a2a2a">
        <v-card-title class="font-weight-bold justify-center">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Training unvollständig
        </v-card-title>
        <v-card-text class="white--text mt-4">
          <p>Du hast noch nicht alle geplanten Sätze abgeschlossen. Folgende Übungen sind noch offen:</p>

          <v-list dense color="transparent" class="py-0">
            <v-list-item v-for="ex in incompleteExercises" :key="ex.title" class="px-1">
              <v-list-item-icon class="mr-3">
                <v-icon>mdi-weight-lifter</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ ex.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ ex.completed }} von {{ ex.planned }} Sätzen erledigt</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p class="mt-4">Möchtest du das Training trotzdem beenden?</p>
        </v-card-text>
        <v-card-actions class="pa-4 d-flex justify-space-around">
          <v-btn text color="grey" @click="showIncompleteDialog = false">Weitermachen</v-btn>
          <v-btn color="primary" depressed @click="finishAnyway">Trotzdem beenden</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <transition name="grow">
      <div v-if="getSelectedExercise" class="overlay">
        <v-card class="dialog-card fullscreen px-4" dark>
          <v-carousel v-if="!getSelectedExercise.isSuperset" height="220px" hide-delimiters :show-arrows="selectedExerciseImageCarousel.length > 1">
            <v-carousel-item class="text-center" v-for="(item, i) in selectedExerciseImageCarousel" :key="i">
              <v-img contain :src="item.src" height="200px">
                <template #placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center"><v-progress-circular indeterminate /></v-row>
                </template>
              </v-img>
              <span class="caption-text">
                {{ selectedExerciseImageCarousel.length > 1 ? "Schritt " + (i + 1) + ": " : "" }} {{ item.description }}</span
              >
            </v-carousel-item>
          </v-carousel>
          <div v-else class="superset-image-container d-flex justify-center align-center" style="height: 220px">
            <div
              v-for="subEx in getSelectedExercise.exercises"
              :key="subEx.instanceId"
              class="superset-avatar-wrapper mx-2"
              :class="{ 'active-superset-exercise': getActiveSupersetSubExercise && getActiveSupersetSubExercise.instanceId === subEx.instanceId }"
              @click="showImage(subEx)"
              style="cursor: pointer">
              <div>
                <v-avatar size="100" class="elevation-4">
                  <v-img :src="getImageForExercise(subEx)" />
                </v-avatar>
                <div class="caption text-center mt-2 white--text text-truncate" style="max-width: 100px">
                  {{ subEx.title }}
                </div>
              </div>
            </div>
          </div>
          <v-card-text ref="dialogContent" class="dialog-content mt-3 px-0">
            <v-alert
              style="background: rgba(255, 255, 255, 0.05)"
              v-if="selectedExerciseMethodDetail"
              :color="selectedExerciseMethodDetail.color"
              border="left"
              elevation="2"
              colored-border
              class="mb-4 intensity-alert"
              dark>
              <div class="d-flex align-center">
                <v-icon class="mr-3">{{ selectedExerciseMethodDetail.icon }}</v-icon>
                <div class="intensity-alert-title">{{ selectedExerciseMethodDetail.descriptionShort }}</div>
                <v-btn icon small class="ml-auto" @click="toggleIntensityAlertExpansion"
                  ><v-icon>{{ isIntensityAlertExpanded ? "mdi-chevron-up" : "mdi-information-outline" }}</v-icon></v-btn
                >
              </div>
              <v-expand-transition>
                <div v-show="isIntensityAlertExpanded" class="intensity-details-expanded mt-2">
                  <v-divider class="my-3"></v-divider>
                  <div class="px-1 content-wrapper">
                    <p class="subtitle-1 font-weight-bold">{{ selectedExerciseMethodDetail.details.summary }}</p>
                    <p class="body-2 mb-4">{{ selectedExerciseMethodDetail.description }}</p>
                    <div v-if="selectedExerciseMethodDetail.details.advantages && selectedExerciseMethodDetail.details.advantages.length">
                      <h4
                        class="mt-4 mb-1 text-uppercase text-caption"
                        :style="{ color: getSectionHeadlineColor(selectedExerciseMethodDetail.color) }">
                        Vorteile
                      </h4>
                      <v-list dense color="transparent" class="pa-0">
                        <v-list-item
                          v-for="(advantage, index) in selectedExerciseMethodDetail.details.advantages"
                          :key="'adv-' + index"
                          class="px-0 py-1">
                          <v-list-item-icon class="mr-2 my-0"
                            ><v-icon small :color="getIconColorForItem(advantage, 'advantage')">{{ advantage.icon }}</v-icon></v-list-item-icon
                          >
                          <v-list-item-content class="py-0"
                            ><v-list-item-title class="body-2 white--text">{{ advantage.text }}</v-list-item-title></v-list-item-content
                          >
                        </v-list-item>
                      </v-list>
                    </div>
                    <div v-if="selectedExerciseMethodDetail.details.whyDoIt && selectedExerciseMethodDetail.details.whyDoIt.length">
                      <h4
                        class="mt-4 mb-1 text-uppercase text-caption"
                        :style="{ color: getSectionHeadlineColor(selectedExerciseMethodDetail.color, true) }">
                        Warum diese Technik?
                      </h4>
                      <v-list dense color="transparent" class="pa-0">
                        <v-list-item
                          v-for="(reason, index) in selectedExerciseMethodDetail.details.whyDoIt"
                          :key="'reason-' + index"
                          class="px-0 py-1">
                          <v-list-item-icon class="mr-2 my-0"
                            ><v-icon small :color="getIconColorForItem(reason, 'reason')">{{ reason.icon }}</v-icon></v-list-item-icon
                          >
                          <v-list-item-content class="py-0"
                            ><v-list-item-title class="body-2 white--text">{{ reason.text }}</v-list-item-title></v-list-item-content
                          >
                        </v-list-item>
                      </v-list>
                    </div>
                  </div>
                  <v-divider class="mt-4 mb-2"></v-divider>
                  <div class="d-flex justify-end pa-1">
                    <v-btn text small :color="selectedExerciseMethodDetail.color" @click="toggleIntensityAlertExpansion"
                      ><v-icon left small>mdi-chevron-up</v-icon>Weniger anzeigen</v-btn
                    >
                  </div>
                </div>
              </v-expand-transition>
            </v-alert>
            <v-card flat class="mb-4 pa-4 equipment-card" :class="{ 'orange--border': hasWarmup && slideIndex === 0 }">
              <template
                v-if="
                  getSelectedExercise &&
                  getSelectedExercise.general_exercise_settings &&
                  getSelectedExercise.general_exercise_settings.filter((s) => s.value).length
                ">
                <div class="preset-label">Geräteeinstellungen</div>
                <div class="plan-settings">
                  <v-chip
                    v-for="(setting, i) in getSelectedExercise.general_exercise_settings.filter((s) => s.value)"
                    :key="'device-' + i"
                    outlined
                    small
                    class="preset-chip mr-2 mb-2">
                    {{ setting.text }}: {{ setting.value }}
                  </v-chip>
                  <span
                    v-if="
                      !getSelectedExercise.general_exercise_settings || !getSelectedExercise.general_exercise_settings.filter((s) => s.value).length
                    "
                    class="grey--text text--lighten-1 text-caption">
                    Keine spezifischen Geräteeinstellungen hinterlegt.
                  </span>
                </div>
              </template>
              <template v-else>
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="preset-label">{{ slideLabel }}</div>
                  <div v-if="!getSelectedExercise.isSuperset && (hasWarmup || (displayPlanSettings && displayPlanSettings.length > 0))">
                    <v-btn icon @click="prevSlide" :disabled="slideIndex === 0"><v-icon>mdi-chevron-left</v-icon></v-btn>
                    <v-btn icon @click="nextSlide" :disabled="slideIndex >= totalSlides - 1"><v-icon>mdi-chevron-right</v-icon></v-btn>
                  </div>
                </div>
                <div class="plan-settings">
                  <template v-if="hasWarmup && slideIndex === 0">
                    <v-chip v-for="(w, i) in warmupSettings" :key="'w' + i" outlined small class="preset-chip orange mr-2 mb-2"
                      >{{ w.label }}: {{ w.value }}{{ w.unit || "" }}</v-chip
                    >
                  </template>
                  <template v-else-if="displayPlanSettings && displayPlanSettings.length > 0">
                    <v-chip v-for="(s, i) in displayPlanSettings" :key="'p' + i" outlined small class="preset-chip mr-2 mb-2"
                      >{{ s.label }}: {{ s.value }}</v-chip
                    >
                  </template>
                  <template v-else><span class="grey--text text--lighten-1 text-caption">Keine spezifischen Satzvorgaben.</span></template>
                </div>
              </template>
            </v-card>
            <template v-if="isSensoProActive">
              <v-card flat class="mb-4 pa-4 finished-sets-card">
                <v-card-title class="pa-0 pb-2">Notizen</v-card-title>
                <div v-if="currentSensoProSet">
                  <div class="d-flex align-center" v-if="!editingNote">
                    <p class="mb-0 body-1"><strong>Heute:</strong> {{ currentSensoProSet.find((s) => s.text === "note").value || "Keine Notiz" }}</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="editCurrentNote"><v-icon>mdi-pencil</v-icon></v-btn>
                  </div>
                  <div v-else>
                    <v-textarea v-model="editableNote" outlined rows="2" label="Heutige Notiz bearbeiten"></v-textarea>
                    <v-card-actions class="pa-0">
                      <v-spacer></v-spacer>
                      <v-btn text @click="cancelEditNote">Abbrechen</v-btn>
                      <v-btn color="primary" @click="saveCurrentNote">Speichern</v-btn>
                    </v-card-actions>
                  </div>
                  <v-divider class="my-4"></v-divider>
                </div>

                <div v-if="historicalSensoProNotes.length > 0">
                  <div v-for="(item, i) in historicalSensoProNotes" :key="i" class="mb-2">
                    <div class="text-caption grey--text">{{ item.date }}</div>
                    <p class="body-2 mb-0">{{ item.note }}</p>
                  </div>
                </div>
                <div v-else class="text-center grey--text py-2 text-caption">Noch keine früheren Notizen vorhanden.</div>
              </v-card>
            </template>
            <template v-else>
              <v-card flat class="mb-4 pa-4 finished-sets-card">
                <div class="history-nav d-flex justify-space-between align-center mb-4">
                  <v-btn icon @click="navigateHistory(1)" :disabled="historyViewIndex >= historicalSessions.length">
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <div class="text-subtitle-1 font-weight-bold text-center">
                    {{ historyTitle }}
                  </div>
                  <v-btn icon @click="navigateHistory(-1)" :disabled="historyViewIndex === 0">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>

                <v-divider class="mb-2"></v-divider>

                <transition name="table-container-transition" mode="out-in">
                  <div :key="historyViewIndex">
                    <v-simple-table class="completed-table" style="table-layout: fixed" v-if="setsToDisplay.length > 0">
                      <thead>
                        <tr>
                          <th v-if="setsToDisplay && setsToDisplay.find((set) => set.subExerciseId)" style="width: 48px"></th>

                          <th style="width: 40px">#</th>
                          <th>Details</th>
                          <th style="width: 100px; text-align: center">Feedback</th>
                        </tr>
                      </thead>
                      <transition-group tag="tbody" name="row">
                        <tr
                          v-for="(set, idx) in setsToDisplay"
                          :key="set.id || idx"
                          @click="isTodaySelected ? promptDeleteSet(idx, set) : null"
                          :class="{ 'clickable-row': isTodaySelected }">
                          <td v-if="set.subExerciseId">
                            <v-avatar size="28" class="clickable-avatar" @click.stop="showImage(getSubExerciseForSet(set))">
                              <v-img :src="getImageForExercise(getSubExerciseForSet(set))" />
                            </v-avatar>
                          </td>
                          <td>
                            <template v-if="hasWarmup && idx === 0">
                              <span v-if="set.skipped" class="grey--text text--lighten-1">--</span>
                              <span v-else style="margin-left: -5px">🔥</span>
                            </template>
                            <template v-else>{{ hasWarmup ? realSetNumber(idx) : idx + 1 }}</template>
                          </td>
                          <td :class="{ 'orange--text': hasWarmup && idx === 0 && !set.skipped, 'grey--text text--lighten-1': set.skipped }">
                            {{ set.skipped ? "Warmup übersprungen" : formatSetText(set) }}
                          </td>
                          <td style="text-align: center" @click.stop>
                            <v-menu
                              v-if="!set.skipped"
                              v-model="set.showMenu"
                              offset-y
                              top
                              :close-on-content-click="false"
                              transition="scale-transition">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  v-bind="attrs"
                                  v-on="on"
                                  :color="getFeedbackIcon(set, idx).color"
                                  :disabled="!isTodaySelected && !hasRating(set)"
                                  class="rating-icon"
                                  @click="isTodaySelected && !hasRating(set) ? openRetrospectiveFeedback(set, idx) : openRatingTooltip(set)">
                                  {{ getFeedbackIcon(set, idx).icon }}
                                </v-icon>
                              </template>

                              <v-card
                                v-if="isTodaySelected && (hasRating(set) || getWeightDeviation(set, idx).deviated)"
                                class="feedback-tooltip-card">
                                <div class="font-weight-bold mb-2 d-flex align-center">
                                  <v-icon left :color="getFeedbackIcon(set, idx).color">{{ getFeedbackIcon(set, idx).icon }}</v-icon>
                                  Feedback
                                </div>
                                <div v-if="getWeightDeviation(set, idx).deviated" class="caption">
                                  {{ getWeightDeviation(set, idx).text }}
                                </div>
                                <div v-if="hasRating(set)" class="caption" :class="{ 'mt-2 pt-2 border-top': getWeightDeviation(set, idx).deviated }">
                                  {{ getTooltipText(set).main }}
                                </div>
                                <div v-if="getTooltipText(set).sub" class="caption mt-2 pt-2 border-top">
                                  <v-icon small left>mdi-arrow-right-top-bold</v-icon>
                                  {{ getTooltipText(set).sub }}
                                </div>
                              </v-card>
                            </v-menu>
                          </td>
                        </tr>
                      </transition-group>
                    </v-simple-table>
                    <div v-else class="no-sets text-center pa-4 grey--text">An diesem Tag wurden keine Sätze für diese Übung aufgezeichnet.</div>
                  </div>
                </transition>
              </v-card>
            </template>

            <component
              v-if="isTodaySelected && exerciseForInput && !currentSensoProSet"
              :is="currentInputComponent"
              :exercise="exerciseForInput"
              :completed-sets="completedSets"
              :completed-sets-array="completedSetsArray"
              :warmup="hasWarmup"
              :plan-settings="getCurrentSatzVorgaben"
              @add-set="handleAddSet"
              @update-slide-index="slideIndex = $event"
              ref="inputComponentSection">
              <template #add-button
                ><v-btn color="orange" class="ma-2">{{ addSetLabel }}</v-btn></template
              >
            </component>
          </v-card-text>
        </v-card>
      </div>
    </transition>

    <transition name="rest-timer-overlay-fade">
      <div v-if="showFeedbackOverlay" class="feedback-overlay d-flex justify-center align-center">
        <v-card dark class="feedback-card pa-5 text-center elevation-20 rounded-xl d-flex flex-column" width="95%" max-width="450px">
          <div style="position: absolute; top: 8px; right: 8px; z-index: 10">
            <v-btn icon small @click="showInfoView" class="mr-1" v-if="feedbackState !== 'info'"><v-icon>mdi-information-outline</v-icon></v-btn>
            <v-btn icon small @click="skipFeedback" v-if="feedbackState !== 'info'"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <div class="mb-4"><v-img src="/img/roody_logo.png" height="50px" contain /></div>

          <template v-if="isTimerActive">
            <div
              class="text-h5 font-weight-bold mb-1 status-title"
              :style="{
                color: restTimerRemaining <= 0 && overtimeSeconds <= 0 ? $vuetify.theme.themes.dark.success : restTimerProgressColor,
                letterSpacing: '0.03em !important',
                textTransform: 'uppercase',
              }">
              {{ restTimerRemaining > 0 ? "Pause" : overtimeSeconds > 0 ? "Überzeit" : "Fertig!" }}
            </div>
            <div class="my-auto d-flex flex-column align-center">
              <v-progress-circular
                :rotate="-90"
                :size="$vuetify.breakpoint.xsOnly ? 170 : 180"
                :width="$vuetify.breakpoint.xsOnly ? 12 : 14"
                :value="restTimerProgress"
                :color="restTimerProgressColor"
                class="my-4">
                <span class="countdown-text" :class="{ 'countdown-text-smaller': isCountdownTextTooLong }">
                  {{
                    formatCountdownDisplay(
                      restTimerRemaining > 0 ? restTimerRemaining : overtimeSeconds,
                      restTimerRemaining <= 0 && overtimeSeconds > 0
                    )
                  }}
                </span>
              </v-progress-circular>
              <div class="text-body-2 grey--text text--lighten-1 mt-2">Geplant: {{ formatTimeForDisplay(plannedRestDuration) }}</div>
            </div>
          </template>

          <v-fade-transition mode="out-in">
            <div v-if="feedbackState === 'rating'" key="rating" class="feedback-section mt-4">
              <div v-if="weightDeviationStatus === 'higher'">
                <h3 class="font-weight-bold mb-2" style="color: #a5d6a7">🎉 Super! Gewicht gesteigert!</h3>
                <p class="body-1 mb-3">Wie hat sich das neue Gewicht angefühlt?</p>
                <v-row justify="center" no-gutters align="start">
                  <div class="text-center mx-3">
                    <v-btn icon large color="green" @click="handleSmileyClick('easy')"><v-icon size="48">mdi-weight-lifter</v-icon></v-btn>
                    <div class="smiley-label green--text text--lighten-2">Noch zu leicht</div>
                  </div>
                  <div class="text-center mx-3">
                    <v-btn icon large color="orange" @click="handleSmileyClick('medium')"
                      ><v-icon size="48">mdi-emoticon-happy-outline</v-icon></v-btn
                    >
                    <div class="smiley-label orange--text text--lighten-2">Perfekt so</div>
                  </div>
                  <div class="text-center mx-3">
                    <v-btn icon large color="red" @click="handleSmileyClick('hard')"><v-icon size="48">mdi-emoticon-dead-outline</v-icon></v-btn>
                    <div class="smiley-label red--text text--lighten-2">Doch zu schwer</div>
                  </div>
                </v-row>
              </div>

              <div v-else-if="weightDeviationStatus === 'lower'">
                <h3 class="font-weight-bold mb-2" style="color: #ffcc80">Macht nichts, jeder Tag ist anders!</h3>
                <p class="body-1 mb-3">Bewerte die Schwierigkeit mit dem angepassten Gewicht:</p>
                <v-row justify="center" no-gutters align="start">
                  <div class="text-center mx-3">
                    <v-btn icon large color="red" @click="handleSmileyClick('hard')"><v-icon size="48">mdi-emoticon-dead-outline</v-icon></v-btn>
                    <div class="smiley-label red--text text--lighten-2">Immer noch schwer</div>
                  </div>
                  <div class="text-center mx-3">
                    <v-btn icon large color="orange" @click="handleSmileyClick('medium')"
                      ><v-icon size="48">mdi-emoticon-happy-outline</v-icon></v-btn
                    >
                    <div class="smiley-label orange--text text--lighten-2">Genau richtig</div>
                  </div>
                </v-row>
              </div>

              <div v-else>
                <v-card-title class="justify-center font-weight-bold pt-0">Wie war der Satz?</v-card-title>
                <p class="body-1 mb-3">Bewerte die Schwierigkeit:</p>
                <v-row justify="center" no-gutters align="start">
                  <div class="text-center mx-3">
                    <v-btn icon large color="red" @click="handleSmileyClick('hard')"><v-icon size="48">mdi-emoticon-dead-outline</v-icon></v-btn>
                    <div class="smiley-label red--text text--lighten-2">Zu Schwer</div>
                  </div>
                  <div class="text-center mx-3">
                    <v-btn icon large color="orange" @click="handleSmileyClick('medium')"
                      ><v-icon size="48">mdi-emoticon-happy-outline</v-icon></v-btn
                    >
                    <div class="smiley-label orange--text text--lighten-2">Perfekt</div>
                  </div>
                  <div class="text-center mx-3">
                    <v-btn icon large color="green" @click="handleSmileyClick('easy')"><v-icon size="48">mdi-weight-lifter</v-icon></v-btn>
                    <div class="smiley-label green--text text--lighten-2">Zu Leicht</div>
                  </div>
                </v-row>
              </div>
            </div>

            <div v-else-if="feedbackState === 'show_weights'" key="weights" class="feedback-section mt-4">
              <p class="body-1 mb-3">Passe das Gewicht für dein <b>nächstes Training</b> an:</p>
              <v-row justify="space-around" class="mb-2">
                <v-btn
                  v-for="w in weightSuggestions"
                  :key="w"
                  @click="adjustedWeight = w"
                  :outlined="adjustedWeight !== w"
                  :color="adjustedWeight === w ? (selectedDifficulty === 'easy' ? 'green' : 'red') : 'grey'"
                  class="weight-suggestion-btn">
                  {{ w }} kg
                </v-btn>
              </v-row>
              <v-btn
                small
                text
                color="grey"
                @click="
                  feedbackState = 'rating'
                  adjustedWeight = null
                "
                class="mt-3">
                Abbrechen
              </v-btn>
            </div>

            <div v-else-if="feedbackState === 'info'" key="info" class="feedback-section-info">
              <v-card-title class="justify-center font-weight-bold pt-0">Dein Feedback zählt!</v-card-title>
              <v-card-text class="white--text mt-2 text-left">
                <p class="body-2">Dein Feedback nach jedem Satz hilft uns oder deinem Trainer, deinen Trainingsplan kontinuierlich zu optimieren.</p>
                <v-divider class="my-3"></v-divider>
                <h4 class="font-weight-bold mb-2 text-subtitle-1">Was bedeuten die Bewertungen?</h4>
                <v-list dense color="transparent" class="info-list">
                  <v-list-item class="mb-2 px-0">
                    <v-list-item-icon class="mr-3"><v-icon color="red">mdi-emoticon-dead-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Zu Schwer</v-list-item-title>
                      <v-list-item-subtitle
                        >Du hast die Wiederholungen kaum oder gar nicht geschafft. Das Gewicht war definitiv zu hoch.</v-list-item-subtitle
                      >
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="mb-2 px-0">
                    <v-list-item-icon class="mr-3"><v-icon color="orange">mdi-emoticon-happy-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Perfekt</v-list-item-title>
                      <v-list-item-subtitle
                        >Der Satz war fordernd, aber du hast alle Wiederholungen mit guter Form geschafft. Genau richtig!</v-list-item-subtitle
                      >
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="px-0">
                    <v-list-item-icon class="mr-3"><v-icon color="green">mdi-weight-lifter</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Zu Leicht</v-list-item-title>
                      <v-list-item-subtitle
                        >Der Satz war einfach und du hättest ohne Probleme deutlich mehr Wiederholungen oder Gewicht bewältigen
                        können.</v-list-item-subtitle
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </div>
          </v-fade-transition>

          <div v-if="feedbackState === 'info'" class="mt-auto">
            <v-btn block large rounded color="primary" @click="hideInfoView" class="elevation-4">
              <v-icon left>mdi-arrow-left</v-icon>
              Zurück
            </v-btn>
          </div>
          <div v-else class="mt-auto">
            <v-checkbox
              v-if="!isTimerActive && !disableFeedbackForExercise"
              v-model="doNotShowAgain"
              label="Für diese Übung nicht mehr fragen"
              dense
              class="mt-4"></v-checkbox>
            <div class="mt-1">
              <v-btn
                block
                x-large
                rounded
                :color="buttonColor"
                @click="finishFeedbackAndCloseOverlay()"
                class="elevation-4"
                :disabled="!isFeedbackCompleted"
                style="min-height: 56px; letter-spacing: 0.05em">
                <v-icon left size="26">{{ buttonIcon }}</v-icon>
                {{ buttonText }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </transition>
    <v-dialog v-model="exerciseDetailDialog" max-width="600px" dark>
      <v-card v-if="detailDialogExercise" class="glass-card-dialog">
        <v-toolbar flat color="transparent">
          <v-toolbar-title class="font-weight-bold">{{ detailDialogExercise.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="exerciseDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-carousel height="350px" hide-delimiters :show-arrows="detailCarouselItems.length > 1">
          <v-carousel-item class="text-center" v-for="(item, i) in detailCarouselItems" :key="i">
            <div>
              <v-img contain :src="item.src" height="280px">
                <template #placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate />
                  </v-row>
                </template>
              </v-img>
              <div class="caption-text grey--text text--lighten-1 pa-3">
                {{ item.description }}
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex"
import WeightRepInput from "./setInputTypes/weightRepInput.vue"
import SensoProInput from "./setInputTypes/sensoProInput.vue"
import TimeInput from "./setInputTypes/timeInput.vue"
import { intensityTechniqueMapping } from "../utils/intensityTechniques.js"
import moment from "moment"
// Oben im <script>

// ...
export default {
  name: "ExerciseList",
  components: { WeightRepInput, TimeInput, SensoProInput }, // HIER HINZUFÜGEN
  isSelectionDialogActive: false,
  selectionDialogMode: "swap", // 'swap' oder 'add'
  selectionDialogExerciseToSwap: null,
  selectionDialogInsertionIndex: 0,
  props: ["initialHeight"],
  data() {
    return {
      editingNote: false,
      editableNote: "",
      slideIndex: 0,
      intensityTechniqueMapping: intensityTechniqueMapping,
      isIntensityAlertExpanded: false,
      isFirstSetBeingAdded: false,
      previousScrollTop: 0,
      previousTbodyHeight: 0,
      showFeedbackOverlay: false,
      isTimerActive: false,
      pendingSet: null,
      feedbackState: "rating",
      selectedDifficulty: null,
      weightSuggestions: [],
      adjustedWeight: null,
      doNotShowAgain: false,
      disableFeedbackForExercise: false,
      plannedRestDuration: 0,
      restTimerRemaining: 0,
      overtimeSeconds: 0,
      restTimerInterval: null,
      pauseStartTime: null,
      isRetrospective: false,
      historyViewIndex: 0,
      retrospectiveSetIndex: null,
      weightDeviationStatus: "on_track",
      showIncompleteDialog: false, // NEU
      isAddingWarmupSet: false,
      imageDialog: false,
      imageDialogSrc: "",
      exerciseDetailDialog: false, // Steuert die Sichtbarkeit des neuen Dialogs
      detailDialogExercise: null,
    }
  },
  computed: {
    ...mapState("runWorkoutStore", ["selectedExerciseIndex", "activeSplit", "groupedExercises", "disabledFeedbackExercises", "completedSetsMap"]),
    ...mapGetters("runWorkoutStore", ["getSelectedExercise", "getActiveSupersetSubExercise", "getCurrentSatzVorgaben"]),
    isSensoProActive() {
      return this.getSelectedExercise?.title === "Senso Pro"
    },
    currentSensoProSet() {
      if (!this.isSensoProActive || !this.isTodaySelected) return null
      return this.completedSetsArray.length > 0 ? this.completedSetsArray[0] : null
    },
    historicalSensoProNotes() {
      if (!this.isSensoProActive || !this.historicalSessions) return []
      const notes = []
      this.historicalSessions.forEach((session) => {
        if (session.steps && session.steps[0] && Array.isArray(session.steps[0])) {
          const noteEntry = session.steps[0].find((s) => s.text === "note")
          if (noteEntry && noteEntry.value) {
            notes.push({ date: moment(session.date).format("DD.MM.YYYY"), note: noteEntry.value })
          }
        }
      })
      return notes
    },
    exerciseForInput() {
      if (this.getSelectedExercise?.isSuperset) {
        return this.getActiveSupersetSubExercise
      }
      return this.getSelectedExercise
    },
    // In runWorkoutExerciseList.vue -> computed

    detailCarouselItems() {
      const ex = this.detailDialogExercise
      if (!ex) return []

      const imgs = []

      // 1. Füge zuerst das Hauptbild hinzu, falls es existiert.
      if (ex.picture) {
        imgs.push({
          src: this.getImageUrl(ex.picture),
          description: "Hauptansicht",
        })
      }

      // 2. Füge dann alle Bilder aus der Anleitung hinzu, aber nur, wenn sie sich vom Hauptbild unterscheiden.
      if (Array.isArray(ex.manual) && ex.manual.length > 0) {
        ex.manual.forEach((m, index) => {
          if (m.picture) {
            const manualImgSrc = this.getImageUrl(m.picture)
            // Verhindere Duplikate, falls das Hauptbild auch das erste Manual-Bild ist.
            if (!imgs.some((img) => img.src === manualImgSrc)) {
              imgs.push({
                src: manualImgSrc,
                description: m.description || `Schritt ${index + 1}`,
              })
            }
          }
        })
      }

      // 3. Fallback: Wenn nach allem immer noch kein Bild da ist, zeige den Placeholder.
      if (imgs.length === 0) {
        imgs.push({ src: "/img/einmalig/imageUploadPlaceholder.jpg", description: "Kein Bild verfügbar" })
      }

      return imgs
    },
    isTodaySelected() {
      return this.historyViewIndex === 0
    },
    incompleteExercises() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []

      const incomplete = []
      this.activeSplit.exercises.forEach((ex, index) => {
        // Logic for supersets
        if (ex.isSuperset) {
          ex.exercises.forEach((subEx) => {
            const completedSets = this.getCompletedSetsForSubExercise(ex, subEx)
            const plannedCount = subEx.sets ? subEx.sets.length : 0
            if (completedSets.length < plannedCount) {
              incomplete.push({
                title: `${subEx.title} (Supersatz)`,
                completed: completedSets.length,
                planned: plannedCount,
              })
            }
          })
          return
        }

        // Logic for single exercises
        // Access the map directly for reactivity
        const completedSets = this.completedSetsMap[index] || []
        const realCompletedCount = completedSets.filter((set) => !set.skipped).length

        let plannedCount = ex.sets.length
        if (ex.warmup) {
          plannedCount += 1
        }

        if (realCompletedCount < plannedCount) {
          incomplete.push({
            title: ex.title,
            completed: realCompletedCount,
            planned: plannedCount,
          })
        }
      })
      return incomplete
    },
    currentExerciseHistory() {
      if (!this.getSelectedExercise) return null
      console.log(
        "currentExerciseHistory",
        this.getSelectedExercise._id,
        this.groupedExercises,
        this.groupedExercises.find((ex) => ex.exercise._id === this.getSelectedExercise._id)
      )
      return this.groupedExercises.find((ex) => ex.exercise._id === this.getSelectedExercise._id)
    },
    historicalSessions() {
      if (!this.currentExerciseHistory || !this.currentExerciseHistory.sets) return []
      return this.currentExerciseHistory.sets.slice().sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    setsToDisplay() {
      if (this.historyViewIndex === 0) {
        return this.completedSetsArray
      }
      const historicalSession = this.historicalSessions[this.historyViewIndex - 1]
      if (historicalSession && historicalSession.steps) {
        return historicalSession.steps.map((step) => {
          const setArray = []
          if (step.weight != null) setArray.push({ text: "Gewicht", value: step.weight })
          if (step.reps != null) setArray.push({ text: "Wiederholungen", value: step.reps })
          // Hier könnten zukünftig auch historische Bewertungen/Notizen gemappt werden
          return setArray
        })
      }
      return []
    },
    historyTitle() {
      if (this.historyViewIndex === 0) {
        return "Heutiges Training"
      }
      const session = this.historicalSessions[this.historyViewIndex - 1]
      if (session) {
        return `Training vom ${moment(session.date).format("DD.MM.YYYY")}`
      }
      return "Verlauf"
    },
    warmupOnly() {
      if (!this.activeSplit) return []
      const map = this.completedSetsMap
      return this.activeSplit.exercises
        .map((ex, i) => {
          const completedSets = map[i] || []
          // Zähle nur abgeschlossene Sätze, die auch Aufwärmsätze sind
          const completedWarmupCount = completedSets.filter((s) => s.isWarmup && !s.skipped).length
          return { ...ex, originalIndex: i, completedSetCount: completedWarmupCount }
        })
        .filter((ex) => !ex.isSuperset && ex.sets.length === 0 && ex.warmup)
    },

    regularExercises() {
      if (!this.activeSplit) return []
      const map = this.completedSetsMap
      return this.activeSplit.exercises
        .map((ex, i) => {
          const allCompletedSets = map[i] || []
          // Zähle nur abgeschlossene Sätze, die KEINE Aufwärmsätze sind
          const completedRegularCount = allCompletedSets.filter((s) => !s.isWarmup && !s.skipped).length
          const plannedSetCount = ex.isSuperset ? ex.exercises[0]?.sets?.length || 0 : ex.sets?.length || 0

          return {
            ...ex,
            originalIndex: i,
            completedSetCount: completedRegularCount, // Hier wird die korrigierte Anzahl verwendet
            plannedSetCount: plannedSetCount,
          }
        })
        .filter((ex) => {
          if (ex.isSuperset) return true
          return !(ex.sets.length === 0 && ex.warmup)
        })
    },
    selectedExerciseImageCarousel() {
      const ex = this.getSelectedExercise
      if (!ex) return []
      const imgs = []
      if (Array.isArray(ex.manual)) {
        ex.manual.forEach((m) => {
          if (m.picture) imgs.push({ src: this.getImageUrl(m.picture), description: m.description })
        })
      } else if (ex.picture) {
        imgs.push({ src: this.getImageUrl(ex.picture) })
      }
      return imgs
    },
    hasWarmup() {
      return !!this.getSelectedExercise?.warmup
    },
    warmupSettings() {
      if (!this.getSelectedExercise || !this.getSelectedExercise.warmup) return []
      let ws = this.getSelectedExercise.warmup.filter((s) => typeof s.value !== "undefined") || []
      const s = []
      const w = ws.find((s) => s.text === "Gewicht")
      if (w) s.push({ label: "Gewicht", value: w.value + "kg" })
      const minR = ws.find((s) => s.text === "Min Wiederholungen")
      const maxR = ws.find((s) => s.text === "Max Wiederholungen")
      if (minR && maxR) {
        s.push({ label: "WH", value: `${minR.value} - ${maxR.value}` })
      } else if (minR) {
        s.push({ label: "WH", value: minR.value })
      } else if (maxR) {
        s.push({ label: "WH", value: maxR.value })
      }
      const d = ws.find((s) => s.text === "Dauer")
      if (d) s.push({ label: "Dauer", value: d.value + "s" })
      return s
    },
    currentPlanSettingsRaw() {
      // Die gesamte Logik steckt bereits in unserem Vuex-Getter.
      // Wir müssen ihn hier nur zurückgeben.
      return this.getCurrentSatzVorgaben || []
    },

    // KORREKTUR 2: Diese Methode funktioniert jetzt dank der obigen Korrektur automatisch
    displayPlanSettings() {
      const currentSetSettings = this.currentPlanSettingsRaw
      if (!Array.isArray(currentSetSettings) || currentSetSettings.length === 0) {
        return []
      }

      const settings = []
      const weight = currentSetSettings.find((s) => s.text === "Gewicht")
      if (weight?.value) {
        settings.push({ label: "Gewicht", value: weight.value + "kg" })
      }

      const minRep = currentSetSettings.find((s) => s.text === "Min Wiederholungen")?.value
      const maxRep = currentSetSettings.find((s) => s.text === "Max Wiederholungen")?.value
      if (minRep && maxRep) {
        settings.push({ label: "WH", value: `${minRep} - ${maxRep}` })
      } else if (minRep || maxRep) {
        settings.push({ label: "WH", value: minRep || maxRep })
      }

      const duration = currentSetSettings.find((s) => s.text === "Dauer")
      if (duration?.value) {
        settings.push({ label: "Dauer", value: duration.value + "s" })
      }

      const pauseSetting = currentSetSettings.find((s) => s.text === "Pause")
      if (pauseSetting?.value > 0) {
        settings.push({ label: "Pause", value: pauseSetting.value + "s" })
      }

      return settings
    },

    // KORREKTUR 3: Macht die Slide-Navigation Supersatz-fähig (obwohl sie ausgeblendet ist)
    totalSlides() {
      if (!this.getSelectedExercise) return 0
      if (this.getSelectedExercise.isSuperset) {
        // Bei einem Supersatz entspricht die Anzahl der "Runden" der Satzanzahl der ersten Sub-Übung
        return this.getSelectedExercise.exercises[0]?.sets?.length || 0
      }
      const c = (this.getSelectedExercise.sets || []).length
      return this.hasWarmup ? c + 1 : c
    },

    // KORREKTUR 4: Zeigt den korrekten Label für den nächsten Satz an
    slideLabel() {
      if (!this.getSelectedExercise) return ""

      if (this.getSelectedExercise.isSuperset) {
        const subEx = this.getActiveSupersetSubExercise
        if (!subEx) return "Supersatz"

        // Zähle, wie viele Sätze für DIESE Sub-Übung schon gemacht wurden
        const completedForThisSubEx = this.getCompletedSetsForSubExercise(this.getSelectedExercise, subEx).length
        return `Vorgabe Satz ${completedForThisSubEx + 1}`
      }

      // Bestehende Logik für Einzelübungen
      if (this.hasWarmup && this.slideIndex === 0) return "🔥 Aufwärmsatz Vorgaben"
      const i = this.hasWarmup ? this.slideIndex : this.slideIndex + 1
      return `Vorgaben Satz ${i}`
    },
    addSetLabel() {
      if (!this.getSelectedExercise) return "Satz hinzufügen"
      if (this.hasWarmup && this.completedSetsArray.length === 0) return "Aufwärmsatz hinzufügen"
      const nI = this.completedSetsArray.length + (this.hasWarmup ? 0 : 1)
      return `Satz ${nI} hinzufügen`
    },
    completedSets() {
      return this.completedSetsArray.length
    },
    completedSetsArray() {
      if (this.selectedExerciseIndex === null) return []
      return this.completedSetsMap[this.selectedExerciseIndex] || []
    },
    currentInputComponent() {
      if (!this.exerciseForInput) return null

      if (this.exerciseForInput.title === "Senso Pro") {
        console.log("Using SensoProInput for Senso Pro exercise")
        return "SensoProInput"
      }

      if (this.exerciseForInput.set_exercise_settings?.some((s) => s.text === "Dauer")) {
        return "TimeInput"
      }

      return "WeightRepInput"
    },
    selectedExerciseMethodDetail() {
      if (this.getSelectedExercise && this.getSelectedExercise.method && this.intensityTechniqueMapping[this.getSelectedExercise.method]) {
        return this.intensityTechniqueMapping[this.getSelectedExercise.method]
      }
      return null
    },
    restTimerProgress() {
      if (this.plannedRestDuration <= 0) return 100
      if (this.restTimerRemaining > 0) {
        const elapsed = this.plannedRestDuration - this.restTimerRemaining
        return (elapsed / this.plannedRestDuration) * 100
      }
      return 100
    },
    restTimerProgressColor() {
      if (this.restTimerRemaining <= 0) return "green accent-3"
      if (this.restTimerRemaining <= 10 && this.restTimerRemaining > 0) return "orange darken-1"
      return this.selectedExerciseMethodDetail?.color || "primary"
    },
    isCountdownTextTooLong() {
      const seconds = this.restTimerRemaining > 0 ? this.restTimerRemaining : this.overtimeSeconds
      return String(seconds).length >= 3
    },
    isFeedbackCompleted() {
      if (this.doNotShowAgain) return true
      if (this.feedbackState === "rating") return !!this.selectedDifficulty
      if (this.feedbackState === "show_weights") return this.adjustedWeight !== null
      return false
    },
    buttonColor() {
      if (!this.isFeedbackCompleted) return "blue-grey darken-1"
      if (this.doNotShowAgain) return "primary"
      const colors = { easy: "green", medium: "orange", hard: "red" }
      return colors[this.selectedDifficulty] || "blue-grey darken-1"
    },
    buttonText() {
      return this.isTimerActive ? "Weiter" : "Bestätigen"
    },
    buttonIcon() {
      return this.isTimerActive ? "mdi-skip-next" : "mdi-check"
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", [
      "setSelectedExerciseIndex",
      "addCompletedSet",
      "removeCompletedSet",
      "clearCompletedSets",
      "addDisabledFeedbackExercise",
      "replaceCompletedSet",
      "updateCompletedSet",
      "swapExerciseTemporarily", // NEU
      "addExerciseTemporarily", // NEU
    ]),

    getPlanForSetAtIndex(setIndex) {
      const mainExercise = this.getSelectedExercise
      if (!mainExercise || setIndex < 0) return []

      // Logik für Supersätze
      if (mainExercise.isSuperset) {
        // Finde heraus, welche Sub-Übung beim N-ten Satz des Supersatzes dran ist
        const subExIndex = setIndex % mainExercise.exercises.length
        const subEx = mainExercise.exercises[subExIndex]
        // Finde heraus, der wievielte Satz es für diese Sub-Übung ist
        const planIndexForSubEx = Math.floor(setIndex / mainExercise.exercises.length)
        return subEx.sets[planIndexForSubEx] || []
      }

      // Logik für Einzelübungen
      else {
        const hasWarmup = mainExercise.warmup && mainExercise.warmup.length > 0
        // Wenn es der erste Satz ist und ein Warmup existiert, nimm die Warmup-Vorgaben
        if (hasWarmup && setIndex === 0) {
          return mainExercise.warmup
        }
        // Ansonsten berechne den korrekten Index für die regulären Sätze
        const regularSetIndex = hasWarmup ? setIndex - 1 : setIndex
        if (regularSetIndex < 0) return []

        return mainExercise.sets[regularSetIndex] || []
      }
    },
    getCardStyle(exercise) {
      if (exercise.method && this.intensityTechniqueMapping[exercise.method]) {
        const color = this.intensityTechniqueMapping[exercise.method].color
        // Wir setzen eine CSS-Variable, die im Style-Block verwendet wird
        return { "--glow-color": color }
      }
      return {}
    },
    getCompletedSetsForSubExercise(superset, subExercise) {
      if (!superset || !subExercise) return []

      // Greife auf das Array aller Sätze für diesen Supersatz-Block zu
      // Wir verwenden die direkt gemappte `completedSetsMap`
      const allSetsForSuperset = this.completedSetsMap[superset.originalIndex] || []

      // Filtere die Sätze, die zur spezifischen Sub-Übung gehören,
      // indem du die 'subExerciseId' prüfst, die wir im Store hinzufügen.
      return allSetsForSuperset.filter((set) => set.subExerciseId === subExercise._id)
    },
    // NEUE METHODEN
    openSwapDialog() {
      this.selectionDialogMode = "swap"
      this.selectionDialogExerciseToSwap = this.getSelectedExercise
      this.isSelectionDialogActive = true
    },
    openAddDialog(index) {
      console.log(index)
      this.$emit("open-add-dialog", index)
    },
    handleSwapSelected({ newExercise, exerciseIndex }) {
      this.swapExerciseTemporarily({ newExercise, exerciseIndex })
    },
    handleAddSelected({ newExercise, atIndex }) {
      // Erstellt ein leeres Set-Array für die neue Übung
      const exerciseWithEmptySets = {
        ...newExercise,
        sets: [],
        warmup: null, // oder Standard-Warmup definieren falls gewünscht
      }
      this.addExerciseTemporarily({ newExercise: exerciseWithEmptySets, atIndex })

      // Optional: Direkt zur neu hinzugefügten Übung wechseln
      this.setSelectedExerciseIndex(atIndex)
    },
    promptFinishWorkout() {
      if (this.incompleteExercises.length > 0) {
        this.showIncompleteDialog = true
      } else {
        this.$router.push({ name: "WorkoutSummary" })
      }
    },

    finishAnyway() {
      this.showIncompleteDialog = false
      this.$router.push({ name: "WorkoutSummary" })
    },
    showInfoView() {
      this.feedbackState = "info"
    },
    hideInfoView() {
      this.feedbackState = "rating"
    },
    skipFeedback() {
      this.finishFeedbackAndCloseOverlay(true)
    },
    finishFeedbackAndCloseOverlay(skipFeedback = false) {
      if (!this.pendingSet) return

      const actualDuration = this.pauseStartTime ? Math.round((Date.now() - this.pauseStartTime) / 1000) : 0
      let finalSet = [...this.pendingSet]

      if (!skipFeedback) {
        if (this.feedbackState === "show_weights" && this.adjustedWeight !== null) {
          finalSet.push({ text: "newWeight", value: this.adjustedWeight })
        } else if (this.weightDeviationStatus === "higher" && this.selectedDifficulty !== "easy" && this.selectedDifficulty !== "hard") {
          const actualWeight = finalSet.find((s) => s.text === "Gewicht")?.value
          if (actualWeight) {
            finalSet.push({ text: "newWeight", value: actualWeight })
          }
        }

        if (this.selectedDifficulty) {
          finalSet.push({ text: "Difficulty", value: this.selectedDifficulty })
        }
        if (this.doNotShowAgain) {
          this.addDisabledFeedbackExercise(this.getSelectedExercise._id)
        }
      }

      if (this.isTimerActive) {
        finalSet.push({ text: "Tatsächliche Pause", value: actualDuration })
      }

      if (this.isRetrospective) {
        this.updateCompletedSet({
          exerciseIndex: this.selectedExerciseIndex,
          setIndex: this.retrospectiveSetIndex,
          updatedSet: finalSet,
        })
      } else {
        // KORRIGIERTER AUFRUF: Übergibt jetzt das 'isWarmup'-Flag an die Vuex Action.
        this.addCompletedSet({
          set: finalSet,
          isWarmup: this.isAddingWarmupSet,
        })
      }

      this.clearRestTimer()

      this.$nextTick(() => {
        const lastRow = this.$refs.dialogContent?.querySelector(".completed-table tbody tr:last-child")
        if (lastRow) {
          lastRow.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
      })
    },
    open(ex, ev) {
      this.historyViewIndex = 0
      this.setSelectedExerciseIndex(ex.originalIndex)
      this.slideIndex = 0
      this.isIntensityAlertExpanded = false
      this.clearRestTimer()
      this.disableFeedbackForExercise = (this.disabledFeedbackExercises || []).includes(ex._id)
    },
    navigateHistory(direction) {
      const newIndex = this.historyViewIndex + direction
      if (newIndex >= 0 && newIndex <= this.historicalSessions.length) {
        this.historyViewIndex = newIndex
      }
    },
    prevSlide() {
      if (this.slideIndex > 0) this.slideIndex--
    },
    nextSlide() {
      if (this.slideIndex < this.totalSlides - 1) this.slideIndex++
    },

    handleAddSet(set) {
      if (!this.getSelectedExercise) return

      // Setze das Flag, damit wir später wissen, ob es ein Aufwärmsatz war
      this.isAddingWarmupSet = this.hasWarmup && this.completedSetsArray.length === 0

      // Original-Logik zum Starten des Feedbacks wiederhergestellt
      this.pendingSet = set
      if (set.some((s) => s.skipped)) {
        this.finishFeedbackAndCloseOverlay(true)
      } else {
        this.triggerFeedbackFlow()
      }
    },
    onRemoveSet(idx) {
      if (this.hasWarmup && idx === 0) {
        const placeholder = { skipped: true, type: "warmup", id: Date.now(), showMenu: false }
        this.replaceCompletedSet({ idx: this.selectedExerciseIndex, setPos: 0, newSet: placeholder })
      } else {
        this.removeCompletedSet({ idx: this.selectedExerciseIndex, setPos: idx })
      }
    },
    promptDeleteSet(idx, set) {
      if (set.skipped) return
      if (window.confirm(`Möchtest du den Satz ${this.hasWarmup ? (idx === 0 ? "Aufwärm" : this.realSetNumber(idx)) : idx + 1} wirklich löschen?`)) {
        this.onRemoveSet(idx)
      }
    },
    formatSetText(set) {
      if (!Array.isArray(set)) return ""
      let mainText = ""
      const isDurationOnlySet = set.some((e) => e.text === "Dauer")
      if (isDurationOnlySet) {
        mainText = `${parseFloat(set.find((e) => e.text === "Dauer").value).toFixed(0)}s`
      } else {
        const w = set.find((e) => e.text === "Gewicht")?.value
        const r = set.find((e) => /Wiederholungen/.test(e.text))?.value
        if (w != null && r != null) {
          mainText = `${w}kg x ${r}`
        } else if (w != null) {
          mainText = `${w}kg`
        } else if (r != null) {
          mainText = `${r} WH`
        }
      }
      const pauseInfo = set.find((e) => e.text === "Tatsächliche Pause")
      if (pauseInfo && typeof pauseInfo.value === "number") {
        return `${mainText} (P: ${pauseInfo.value}s)`
      }
      return mainText
    },
    getImageForExercise(ex) {
      if (ex.picture) return this.getImageUrl(ex.picture)
      if (ex.manual?.[0]?.picture) return this.getImageUrl(ex.manual[0].picture)
      return "/img/default-exercise.webp"
    },
    getImageUrl(id) {
      if (!id) return "/img/default-exercise.webp"
      let fn = String(id)
      if (fn.includes("/")) {
        fn = fn.substring(fn.lastIndexOf("/") + 1)
      }
      const nWoE = fn.replace(/\.[^/.]+$/, "")
      if (!nWoE) return "/img/default-exercise.webp"
      return `/img/uebungen/${id.replace(/\.[^/.]+$/, "")}.webp`
    },
    isExerciseCompleted(exercise) {
      if (!exercise) return false

      if (exercise.title === "Senso Pro") {
        return exercise.completedSetCount > 0
      }

      // Logic for Supersets
      if (exercise.isSuperset) {
        // A superset is complete if all its sub-exercises are complete.
        return exercise.exercises.every((subEx) => {
          const completedSets = this.getCompletedSetsForSubExercise(exercise, subEx)
          const plannedCount = subEx.sets ? subEx.sets.length : 0
          return completedSets.length >= plannedCount
        })
      }

      if (exercise.sets.length === 0 && exercise.warmup) {
        // KORREKTUR: Hier wird 'completedSetsMap' statt 'getCompletedSets' verwendet.
        const warmupSet = (this.completedSetsMap[exercise.originalIndex] || [])[0]
        return warmupSet && !warmupSet.skipped
      }

      // Logic for regular exercises
      else if (exercise.plannedSetCount > 0) {
        return exercise.completedSetCount >= exercise.plannedSetCount
      }

      return false
    },
    toggleIntensityAlertExpansion() {
      this.isIntensityAlertExpanded = !this.isIntensityAlertExpanded
    },
    getSectionHeadlineColor(baseColor, isSecondary = false) {
      if (isSecondary) {
        return baseColor === "primary"
          ? "orange darken-1"
          : baseColor.includes("darken")
          ? baseColor.replace(/darken-\d/, "darken-3")
          : baseColor + " darken-2"
      }
      return baseColor.includes("lighten") ? baseColor.replace(/lighten-\d/, "") : baseColor.includes("darken") ? baseColor : baseColor + " darken-1"
    },
    getIconColorForItem(item, type) {
      if (type === "advantage") return "green accent-3"
      if (type === "reason") return "light-blue lighten-1"
      return "grey"
    },
    triggerFeedbackFlow() {
      if ((this.disabledFeedbackExercises || []).includes(this.getSelectedExercise._id)) {
        this.finishFeedbackAndCloseOverlay(true)
        return
      }

      // Ermittle die Vorgaben für den Satz, der GERADE ABGESCHLOSSEN wurde
      const planForCompletedSet = this.getPlanForSetAtIndex(this.completedSetsArray.length)

      const plannedWeight = planForCompletedSet.find((s) => s.text === "Gewicht")?.value
      const actualWeight = this.pendingSet.find((s) => s.text === "Gewicht")?.value

      if (plannedWeight !== null && actualWeight !== undefined) {
        if (actualWeight > plannedWeight) {
          this.weightDeviationStatus = "higher"
        } else if (actualWeight < plannedWeight) {
          this.weightDeviationStatus = "lower"
        } else {
          this.weightDeviationStatus = "on_track"
        }
      } else {
        this.weightDeviationStatus = "on_track"
      }

      const pauseSetting = planForCompletedSet.find((s) => s.text === "Pause")
      let plannedPause = 0
      if (pauseSetting && pauseSetting.value && Number(pauseSetting.value) > 0) {
        plannedPause = Number(pauseSetting.value)
      }

      this.showFeedbackOverlay = true
      if (plannedPause > 0 && !this.isRetrospective) {
        this.isTimerActive = true
        this.plannedRestDuration = plannedPause
        this.restTimerRemaining = plannedPause
        this.overtimeSeconds = 0
        this.pauseStartTime = Date.now()
        this.clearRestTimerInterval()
        this.restTimerInterval = setInterval(() => {
          if (this.restTimerRemaining > 0) {
            this.restTimerRemaining--
          } else {
            this.overtimeSeconds++
          }
        }, 1000)
      } else {
        this.isTimerActive = false
      }
    },
    clearRestTimerInterval() {
      if (this.restTimerInterval) {
        clearInterval(this.restTimerInterval)
        this.restTimerInterval = null
      }
    },
    clearRestTimer() {
      this.clearRestTimerInterval()
      this.showFeedbackOverlay = false
      this.isTimerActive = false
      this.plannedRestDuration = 0
      this.restTimerRemaining = 0
      this.overtimeSeconds = 0
      this.pauseStartTime = null
      this.pendingSet = null
      this.feedbackState = "rating"
      this.selectedDifficulty = null
      this.weightSuggestions = []
      this.adjustedWeight = null
      this.doNotShowAgain = false
      this.isRetrospective = false
      this.retrospectiveSetIndex = null
      this.weightDeviationStatus = "on_track"
      this.isAddingWarmupSet = false
    },
    handleSmileyClick(difficulty) {
      this.selectedDifficulty = difficulty
      const hasWeight = this.pendingSet.some((p) => p.text === "Gewicht")

      if (this.weightDeviationStatus === "higher") {
        if (difficulty === "easy" || difficulty === "hard") {
          this.showWeightSuggestions()
        } else {
          // this.finishFeedbackAndCloseOverlay()
        }
        return
      }

      if (difficulty === "medium" || !hasWeight) {
        //this.finishFeedbackAndCloseOverlay()
      } else {
        this.showWeightSuggestions()
      }
    },
    showWeightSuggestions() {
      const currentWeightEntry = this.pendingSet.find((p) => p.text === "Gewicht")
      if (!currentWeightEntry || isNaN(parseFloat(currentWeightEntry.value))) {
        this.finishFeedbackAndCloseOverlay()
        return
      }
      const currentWeight = parseFloat(currentWeightEntry.value)
      let suggestions = []
      if (this.selectedDifficulty === "hard") {
        suggestions = [
          Math.round((currentWeight * 0.95) / 2.5) * 2.5,
          Math.round((currentWeight * 0.9) / 2.5) * 2.5,
          Math.round((currentWeight * 0.85) / 2.5) * 2.5,
        ]
      } else {
        suggestions = [currentWeight + 2.5, currentWeight + 5.0, currentWeight + 7.5]
      }
      this.weightSuggestions = [...new Set(suggestions.filter((w) => w > 0 && w < currentWeight * 2))].sort((a, b) => a - b).slice(0, 3)
      if (this.weightSuggestions.length === 0) {
        this.feedbackState = "rating"
        return
      }
      this.adjustedWeight = this.weightSuggestions[0]
      this.feedbackState = "show_weights"
    },
    getSmileyForSet(set) {
      if (!Array.isArray(set)) {
        return { icon: null, color: null }
      }
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      if (!difficultyEntry) return { icon: "mdi-help-circle-outline", color: "grey" }
      const difficulty = difficultyEntry.value
      switch (difficulty) {
        case "hard":
          return { icon: "mdi-emoticon-dead", color: "red" }
        case "medium":
          return { icon: "mdi-emoticon-happy", color: "orange" }
        case "easy":
          return { icon: "mdi-weight-lifter", color: "green" }
        default:
          return { icon: "mdi-help-circle-outline", color: "grey" }
      }
    },
    getTooltipText(set) {
      const difficulty = set.find((e) => e.text === "Difficulty")?.value
      const newWeight = set.find((e) => e.text === "newWeight")?.value

      let main = "Keine Bewertung abgegeben."
      switch (difficulty) {
        case "hard":
          main = "Bewertung: Zu Schwer"
          break
        case "medium":
          main = "Bewertung: Perfekt"
          break
        case "easy":
          main = "Bewertung: Zu Leicht"
          break
      }

      let sub = null
      if (newWeight != null) {
        sub = `Neues Startgewicht: ${newWeight}kg`
      }

      return { main, sub }
    },
    openRatingTooltip(set) {
      this.completedSetsArray.forEach((s) => {
        if (s !== set) {
          this.$set(s, "showMenu", false)
        }
      })
      this.$set(set, "showMenu", !set.showMenu)
    },
    openRetrospectiveFeedback(set, setIndex) {
      this.pendingSet = set
      this.isRetrospective = true
      this.retrospectiveSetIndex = setIndex
      this.triggerFeedbackFlow()
    },
    // In runWorkoutExerciseList.vue -> methods
    editCurrentNote() {
      if (!this.currentSensoProSet) return
      this.editableNote = this.currentSensoProSet.find((s) => s.text === "note")?.value || ""
      this.editingNote = true
    },
    cancelEditNote() {
      this.editingNote = false
      this.editableNote = ""
    },
    saveCurrentNote() {
      const finalSet = [{ text: "note", value: this.editableNote }]
      // Wir nutzen die bestehende Action, um den Satz an Position 0 zu ersetzen
      this.replaceCompletedSet({
        idx: this.selectedExerciseIndex,
        setPos: 0,
        newSet: finalSet,
      })
      this.editingNote = false
    },
    getPlannedWeightForSet(setIndex) {
      if (!this.getSelectedExercise || setIndex < 0) return null

      const completedSet = this.completedSetsArray[setIndex]
      if (!completedSet) return null

      // --- NEUE Logik für Supersätze ---
      if (this.getSelectedExercise.isSuperset) {
        const subExercise = this.getSubExerciseForSet(completedSet)
        if (!subExercise || !subExercise.sets) return null

        // Zähle, der wievielte Satz es für DIESE Sub-Übung ist.
        const subSetIndex = this.completedSetsArray
          .slice(0, setIndex) // Nimm alle Sätze VOR dem aktuellen
          .filter((s) => s.subExerciseId === subExercise._id).length

        const plan = subExercise.sets[subSetIndex]
        return plan?.find((s) => s.text === "Gewicht")?.value || null
      }

      // --- Bestehende Logik für Einzelübungen, leicht verbessert ---
      const isThisWarmup = this.hasWarmup && completedSet.isWarmup

      if (isThisWarmup) {
        return this.getSelectedExercise.warmup?.find((s) => s.text === "Gewicht")?.value || null
      }

      // Berechne den korrekten Index für die regulären Sätze
      const regularSetsDoneBeforeThis = this.completedSetsArray.slice(0, setIndex).filter((s) => !s.isWarmup).length

      const plan = this.getSelectedExercise.sets[regularSetsDoneBeforeThis]
      return plan?.find((s) => s.text === "Gewicht")?.value || null
    },
    getWeightDeviation(set, setIndex) {
      if (!Array.isArray(set) || set.some((s) => s.skipped)) return { deviated: false }
      const plannedWeight = this.getPlannedWeightForSet(setIndex)
      const actualWeight = set.find((s) => s.text === "Gewicht")?.value

      if (plannedWeight === null || actualWeight === undefined) {
        return { deviated: false }
      }

      if (actualWeight > plannedWeight) {
        return {
          deviated: true,
          color: "light-green accent-3",
          text: `Stark! Vorgabe (${plannedWeight}kg) übertroffen.`,
        }
      }

      if (actualWeight < plannedWeight) {
        return {
          deviated: true,
          color: "deep-orange lighten-2",
          text: `Okay! Vorgabe (${plannedWeight}kg) nicht erreicht.`,
        }
      }

      return { deviated: false }
    },
    hasRating(set) {
      if (!Array.isArray(set)) return false
      return set.some((s) => s.text === "Difficulty")
    },
    getFeedbackIcon(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      if (deviation.deviated) {
        return { icon: "mdi-swap-vertical-bold", color: deviation.color }
      }
      return this.getSmileyForSet(set)
    },
    updateSetRating(setIndex, difficulty) {
      let setToUpdate = [...this.completedSetsArray[setIndex]]
      const setInArray = this.completedSetsArray[setIndex]
      if (setInArray) this.$set(setInArray, "showMenu", false)
      setToUpdate = setToUpdate.filter((item) => item.text !== "Difficulty")
      setToUpdate.push({ text: "Difficulty", value: difficulty })
      this.updateCompletedSet({
        exerciseIndex: this.selectedExerciseIndex,
        setIndex: setIndex,
        updatedSet: setToUpdate,
      })
    },
    realSetNumber(idx) {
      if (!this.hasWarmup) return idx + 1
      let count = 0
      for (let i = 1; i <= idx; i++) {
        if (!this.completedSetsArray[i] || !this.completedSetsArray[i].skipped) {
          count++
        }
      }
      return count
    },
    formatTimeForDisplay(totalSeconds) {
      if (totalSeconds < 0) totalSeconds = 0
      return `${totalSeconds}s`
    },
    formatCountdownDisplay(currentSeconds, isOvertimeValue = false) {
      if (currentSeconds < 0) currentSeconds = 0
      const prefix = isOvertimeValue && currentSeconds > 0 ? "+" : ""
      return `${prefix}${currentSeconds}`
    },
    getSubExerciseForSet(set) {
      if (!this.getSelectedExercise?.isSuperset || !set.subExerciseId) return null
      return this.getSelectedExercise.exercises.find((ex) => ex._id === set.subExerciseId)
    },
    getSetNumber(set, overallIndex) {
      if (!this.getSelectedExercise.isSuperset) {
        // Alte Logik für Einzelübungen
        if (this.hasWarmup) return this.realSetNumber(overallIndex)
        return overallIndex + 1
      }
      // Neue Logik für Supersätze: Zähle nur die Sätze für DIESE Sub-Übung
      const setsForThisSubExercise = this.completedSetsArray.filter((s) => s.subExerciseId === set.subExerciseId)
      return setsForThisSubExercise.findIndex((s) => s.id === set.id || s === set) + 1
    },
    showImage(exercise) {
      if (!exercise) return
      this.detailDialogExercise = exercise
      this.exerciseDetailDialog = true
    },
  },
  watch: {
    getSelectedExercise(newVal, oldVal) {
      if (newVal && (!oldVal || newVal._id !== oldVal._id)) {
        this.slideIndex = 0
        this.isIntensityAlertExpanded = false
        this.disableFeedbackForExercise = (this.disabledFeedbackExercises || []).includes(newVal._id)
      }
      if (!newVal) {
        this.isIntensityAlertExpanded = false
        this.clearRestTimer()
      }
    },
  },
  beforeDestroy() {
    this.clearRestTimerInterval()
  },
}
</script>
<style scoped>
/* NEUE KLASSEN FÜR GLAS-EFFEKT & SUPERSETS */
.equipment-card,
.finished-sets-card {
  background: rgba(44, 44, 46, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}
.glass-card-dialog {
  background: rgba(40, 40, 40, 0.7);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.superset-image-container {
  background: rgba(44, 44, 46, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}
.superset-avatar-wrapper {
  transition: transform 0.3s ease-in-out;
  text-align: center;
}
.active-superset-exercise .v-avatar {
  transform: translateY(-10px);
  border: 4px solid orange;
  box-shadow: 0 0 10px orange !important;
}
.clickable-avatar {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.clickable-avatar:hover {
  transform: scale(1.2);
}
.tooltip-style {
  background-color: rgba(0, 0, 0, 0.8) !important;
  color: white !important;
}
/* ENDE NEUE KLASSEN */

/* ALTE, WIEDERHERGESTELLTE STILE */
.superset-label {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  color: orange;
  opacity: 0.8;
}
.feedback-tooltip-card {
  background-color: #2e2e2e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px !important;
  padding: 12px;
  color: white !important;
}
.feedback-tooltip-card .caption {
  color: rgba(255, 255, 255, 0.7) !important;
}
.caption-container {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
}
.caption-text {
  color: #fff;
  font-size: 0.9rem;
}
.rating-icon:hover,
.pr-activator .v-icon:hover {
  transform: scale(1.2);
  transition: transform 0.2s ease-in-out;
}
.exercise-list {
  background: #000;
}
.section-header {
  margin: 16px 0 8px;
  align-items: center;
  position: relative;
}
.section-title {
  color: #fff;
  font-size: 1rem;
  font-weight: bold;
  align-content: center;
}
.preset-label {
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
}
.card-inner {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgb(255 255 255 / 50%) !important;
  width: 100%;
  padding: 12px;
  /* NEU: Basis-Border und Übergang für alle Karten */
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden; /* Wichtig für den Glow-Effekt */
}
.completed-checkmark {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}
.exercise-card--warmup .card-inner,
.superset-card-inner {
  border-color: orange !important;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.5) !important;
}

/* NEU: Seitlicher Glow für Intensitätstechniken */

.exercise-card-inner--completed {
  box-shadow: 0 1px 6px green !important;
  border: 2px solid green !important;
}
.card-img {
  object-fit: cover;
  margin-right: 16px;
}
.title {
  color: #fff;
  font-size: 1rem !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.intensity-chip {
  font-size: 0.75rem;
  height: auto;
  padding: 2px 6px;
  margin-top: 2px;
  margin-bottom: 2px;
}
.intensity-chip .v-icon {
  font-size: 0.9em;
}
.subtitle-2 {
  color: #ccc;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}
.preset-chip {
  font-size: 1rem;
  margin-right: 6px;
  margin-bottom: 6px;
  padding: 4px 8px;
}
.no-sets {
  color: #bbb;
  font-size: 1rem;
  padding: 8px 0;
}
.plan-settings {
  display: flex;
  flex-wrap: wrap;
  margin-top: 12px;
}
.finished-sets-card {
  margin-bottom: 16px;
}
.completed-table th,
.completed-table td {
  padding: 10px 12px;
  font-size: 0.95rem;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.completed-table tr:last-child td {
  border-bottom: none !important;
}
.completed-table th {
  color: #9e9e9e !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.orange--border {
  border: 2px solid orange !important;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.5) !important;
}
.orange-dot {
  background-color: #ffa500 !important;
}
.overlay {
  position: fixed;
  inset: 0;
  background: #1e1e1e;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
  z-index: 10;
}
.dialog-card.fullscreen {
  width: 100%;
  max-width: 100vw;
  height: 100%;
  border-radius: 0;
  display: flex;
  flex-direction: column;
}
.dialog-content {
  flex: 1;
  overflow-y: auto;
  width: 100%;
}
.exercise-card {
  position: relative;
  width: calc(100% - 16px);
  margin-bottom: 12px;
}
.exercise-card:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 92px;
  bottom: -12px;
  width: 2px;
  background: grey;
  z-index: 0;
  transition: background-color 0.3s ease;
}
.exercise-card--warmup:not(:last-child)::after {
  background: orange !important;
  bottom: -16px !important;
}
.exercise-card.exercise-line-completed:not(.exercise-card--warmup):not(:last-child)::after {
  background: green;
}
.exercise-card--warmup.exercise-line-completed:not(:last-child)::after {
  background: green !important;
}
.section-header::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 56px;
  bottom: -10px;
  width: 2px;
  z-index: 0;
}
.warmup-marker.section-header::after {
  background: orange;
}
.training-marker.section-header::after {
  background: grey;
}
::v-deep .v-list-item--link:before {
  background-color: transparent !important;
}
.table-container-transition-enter-active,
.table-container-transition-leave-active {
  transition: opacity 0.2s ease-out;
}
.table-container-transition-enter,
.table-container-transition-leave-to {
  opacity: 0;
}
.row-enter-active {
  transition: opacity 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.6s ease-out;
}
.row-leave-active {
  transition: opacity 0.35s cubic-bezier(0.75, 0, 0.25, 1), transform 0.35s cubic-bezier(0.75, 0, 0.25, 1),
    background-color 0.35s cubic-bezier(0.75, 0, 0.25, 1);
  position: absolute;
  width: 100%;
  left: 0;
  box-sizing: border-box;
  z-index: 1;
  background-color: rgba(217, 83, 79, 0.65);
  transform-origin: center center;
}
.row-enter {
  opacity: 0;
  transform: translateY(15px) scale(0.97);
  background-color: rgba(40, 167, 69, 0.3);
}
.row-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
  background-color: transparent;
}
.row-leave-to {
  opacity: 0;
  transform: scale(0.85);
  background-color: rgba(217, 83, 79, 0.3);
}
.completed-table tbody tr td {
  background-color: transparent !important;
}
.row-enter td,
.row-enter-to td,
.row-leave-active td,
.row-leave-to td {
  background-color: inherit !important;
}
.row-move {
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}
.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 20, 0.8);
  z-index: 2000;
  backdrop-filter: blur(8px);
}
.feedback-card {
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
  transition: max-height 0.4s ease-in-out;
  overflow: hidden;
}
.feedback-section {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.status-title {
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}
.countdown-text {
  font-size: 4rem;
  line-height: 1;
  font-weight: 900;
  font-family: "Roboto Condensed", "Roboto", sans-serif;
  letter-spacing: -0.02em;
}
.countdown-text-smaller {
  font-size: 3.5rem !important;
}
.completed-table tbody .clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}
.completed-table tbody .clickable-row:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.clickable-row td:last-child .v-icon {
  cursor: help;
}
.weight-suggestion-btn {
  flex-grow: 1;
  margin: 0 4px;
}
.rest-timer-overlay-fade-enter-active,
.rest-timer-overlay-fade-leave-active {
  transition: opacity 0.25s ease-in-out;
}
.rest-timer-overlay-fade-enter,
.rest-timer-overlay-fade-leave-to {
  opacity: 0;
}
.smiley-label {
  font-size: 0.75rem;
  margin-top: 4px;
  font-weight: 500;
}
.info-list .v-list-item-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  word-wrap: break-word !important;
}
.v-menu__content {
  z-index: 2001 !important;
}
.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
@media (max-width: 380px) {
  .countdown-text {
    font-size: 3.5rem;
  }
  .countdown-text-smaller {
    font-size: 3rem !important;
  }
  .feedback-card {
    padding: 12px !important;
  }
  .text-h5.status-title {
    font-size: 1.3rem !important;
  }
}
.dialog-content::before {
  content: "";
  position: absolute;
  top: -1px; /* Korrigiert, um direkt am Rand zu sein */
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(to bottom, #1e1e1e, transparent);
  z-index: 5;
  pointer-events: none;
}

/* Spotlight-Effekt für die Übungsliste */
.exercise-card {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.is-inactive-exercise-focus {
  opacity: 0.4;
  transform: scale(0.95);
}
.is-active-exercise-focus {
  opacity: 1;
  transform: scale(1.02);
}

/* Verbesserte Typografie für die Vorgaben-Chips */
.preset-chip {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.preset-chip .v-icon {
  margin-right: 6px;
  color: rgba(255, 255, 255, 0.6);
}
</style>
